/* eslint-disable max-lines */
import {
  DatabaseAuto,
  IadminEcommerceAnalytics,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser,
  IcompanyInternalSalesAnalytics,
  IcreditNote,
  IdataArrayResponse,
  IdeleteOneResponse,
  IecommerceSalesAnalytics,
  IfilterProps,
  IgrossProfitAnalytics,
  Iinvoice,
  IinvoiceAnalytics,
  InetIncomeBeforeTaxes,
  InetProfitOrLossAnalytics,
  InonOperatingRevenueAnalytics,
  IoperatingIncomeAnalytics,
  Ireceipt,
  IsaleEventAnalytics,
  ISalesAnalytics,
  ItaxAnalytics,
  IvendorBillSalesAnalytics,
  TinvoiceRequest
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

type TinvoiceProps = Iinvoice<number, IcommerceUser>;

/**
 * Represents an invoice in the system.
 * This class provides methods to manage and track invoices,
 * including creation, retrieval, updating, and deletion of invoices,
 * as well as various analytics and reporting capabilities.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TinvoiceProps - The type definition for invoice properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Invoice} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const invoice = await Invoice.getAll();
 *
 * const invoice = await Invoice.filterAll({
 *
 * });
 *
 * const invoice = await Invoice.bulkDelete({
 *
 * });
 *
 * const invoice = await Invoice.getOne('resourceId');
 *
 * const invoice = await Invoice.remove();
 *
 * ```
 */
export class Invoice extends DatabaseAuto {
  /** Base API endpoint for invoice operations */
  static readonly baseUrl = '/invoice';

  /** Unique reference ID for the invoice */
  readonly urId: TinvoiceProps['urId'];

  /** Due date for the invoice payment */
  readonly dueDate: TinvoiceProps['dueDate'];

  /** Current status of the invoice */
  readonly invoiceStatus: TinvoiceProps['invoiceStatus'];

  /** List of payment receipts associated with the invoice */
  readonly payments: Ireceipt[] = [];

  /** List of credit notes applied to this invoice */
  readonly creditNotes: IcreditNote[] = [];

  /** Unique number identifying the invoice */
  readonly invoiceNumber: TinvoiceProps['invoiceNumber'];

  /** Related invoice information and line items */
  readonly invoiceRelated: TinvoiceProps['invoiceRelated'];

  /** Remaining balance due on the invoice */
  readonly balanceDue: TinvoiceProps['balanceDue'];

  /** Total amount of payments received */
  readonly totalPayments: TinvoiceProps['totalPayments'];

  /** Total amount of credit notes applied */
  readonly totalCreditNotes: TinvoiceProps['totalCreditNotes'];

  /** E-commerce vendors associated with this invoice */
  readonly ecommerceVendors: TinvoiceProps['ecommerceVendors'];

  /** Company associated with the invoice */
  readonly company: TinvoiceProps['company'];

  /**
   * Creates a new instance of Invoice.
   * @param {TinvoiceProps} data - The invoice data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TinvoiceProps) {
    super(data);

    this.urId = data.urId;
    this.dueDate = new Date(data.dueDate);
    this.invoiceStatus = data.invoiceStatus;
    this.payments = data.payments || [];
    this.creditNotes = data.creditNotes || [];
    this.invoiceNumber = data.invoiceNumber;
    this.invoiceRelated = data.invoiceRelated;
    this.balanceDue = data.balanceDue;
    this.totalPayments = data.totalPayments;
    this.totalCreditNotes = data.totalCreditNotes;
    this.ecommerceVendors = data.ecommerceVendors;
    this.company = data.company;
  }

  /**
   * Retrieves admin ecommerce sales analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IadminEcommerceAnalytics>} Analytics data for admin ecommerce sales
   * @throws {Error} If the request fails
   */
  static getAdminEcommerceSalesAnalytics(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IadminEcommerceAnalytics>(
        `/${Invoice.baseUrl}/admin-ecommerce-sales-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves company internal sales analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IcompanyInternalSalesAnalytics>} Analytics data for internal sales
   * @throws {Error} If the request fails
   */
  static getCompanyInternalSalesAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<IcompanyInternalSalesAnalytics>(
        `${Invoice.baseUrl}/company-internal-sales-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves ecommerce sales analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IecommerceSalesAnalytics>} Analytics data for ecommerce sales
   * @throws {Error} If the request fails
   */
  static getEcommerceSalesAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<IecommerceSalesAnalytics>(
        `${Invoice.baseUrl}/ecommerce-sales-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves invoice analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IinvoiceAnalytics>} General invoice analytics data
   * @throws {Error} If the request fails
   */
  static getInvoiceAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<IinvoiceAnalytics>(
        `${Invoice.baseUrl}/invoice-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves sales analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<ISalesAnalytics>} General sales analytics data
   * @throws {Error} If the request fails
   */
  static getSalesAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<ISalesAnalytics>(
        `${Invoice.baseUrl}/sales-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves vendor bill analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IvendorBillSalesAnalytics>} Analytics data for vendor bills
   * @throws {Error} If the request fails
   */
  static getVendorBillAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<IvendorBillSalesAnalytics>(
        `${Invoice.baseUrl}/vendor-bill-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves tax analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<ItaxAnalytics>} Tax-related analytics data
   * @throws {Error} If the request fails
   */
  static getTaxesAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<ItaxAnalytics>(
        `${Invoice.baseUrl}/tax-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves ecommerce tax analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<ItaxAnalytics>} Ecommerce-specific tax analytics
   * @throws {Error} If the request fails
   */
  static getTaxesAnalyticsEcommerce = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<ItaxAnalytics>(
        `${Invoice.baseUrl}/tax-analytics-ecommerce`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves gross profit analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IgrossProfitAnalytics>} Gross profit analytics data
   * @throws {Error} If the request fails
   */
  static getGrossProfitAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<IgrossProfitAnalytics>(
        `${Invoice.baseUrl}/gross-profit-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves net income before taxes analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<InetIncomeBeforeTaxes>} Net income before taxes analytics
   * @throws {Error} If the request fails
   */
  static getNetIncomeBeforeTaxesAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<InetIncomeBeforeTaxes>(
        `${Invoice.baseUrl}/net-income-before-taxes-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves net profit or loss analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<InetProfitOrLossAnalytics>} Net profit or loss analytics
   * @throws {Error} If the request fails
   */
  static getNetProfitOrLossAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<InetProfitOrLossAnalytics>(
        `${Invoice.baseUrl}/net-profit-or-loss-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves non-operating revenue analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<InonOperatingRevenueAnalytics>} Non-operating revenue analytics
   * @throws {Error} If the request fails
   */
  static getNonOperatingRevenueAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<InonOperatingRevenueAnalytics>(
        `${Invoice.baseUrl}/non-operating-revenue-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves operating income analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IoperatingIncomeAnalytics>} Operating income analytics
   * @throws {Error} If the request fails
   */
  static getOperatingIncomeAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<IoperatingIncomeAnalytics>(
        `${Invoice.baseUrl}/operating-income-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves sale event analytics.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IsaleEventAnalytics>} Sale event analytics data
   * @throws {Error} If the request fails
   */
  static getSaleEventAnalytics(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IsaleEventAnalytics>(
        `/${Invoice.baseUrl}/sale-event-analytics`,
        filter
      );

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves all invoices with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: Invoice[] }>}
   * An object containing the total count and an array of invoices
   * @throws {Error} If there's an error fetching the invoices
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TinvoiceProps>>(`${Invoice.baseUrl}?${query}`);
    const invoices = await lastValueFrom(observer$);

    return {
      count: invoices.count,
      dataArr: invoices.data.map(val => new Invoice(val))
    };
  }

  /**
   * Filters invoices based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: Invoice[] }>}
   * An object containing the total count and an array of filtered invoices
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TinvoiceProps>>(
        `${Invoice.baseUrl}/filter`,
        filter
      );
    const invoices = await lastValueFrom(observer$);

    return {
      count: invoices.count,
      dataArr: invoices.data.map(val => new Invoice(val))
    };
  }

  /**
   * Retrieves a single invoice by its ID.
   * @param {string} resourceId - The unique identifier of the invoice to retrieve
   * @returns {Promise<Invoice>} The requested invoice
   * @throws {Error} If the invoice is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TinvoiceProps>(`${Invoice.baseUrl}/${resourceId}`);
    const invoice = await lastValueFrom(observer$);

    return new Invoice(invoice);
  }

  /**
   * Creates a new invoice record.
   * @param {TinvoiceRequest<true>} payload - The invoice data to create
   * @returns {Promise<Invoice>} The newly created invoice
   * @throws {Error} If the creation fails
   */
  static async add(payload: TinvoiceRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TinvoiceProps>(`${Invoice.baseUrl}`, payload);

    return new Invoice(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple invoices based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for identifying which invoices to delete
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   * @throws {Error} If the bulk delete operation fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${Invoice.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current invoice with new values.
   * @param {TinvoiceRequest} payload - The data to update the invoice with
   * @returns {Promise<Invoice>} The updated invoice
   * @throws {Error} If the update operation fails
   */
  async update(payload: TinvoiceRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<TinvoiceProps>(
        `${Invoice.baseUrl}/${this._id}`,
        payload
      );

    return new Invoice(await lastValueFrom(observer$));
  }

  /**
   * Removes the current invoice from the system.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} If the deletion fails or the invoice doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Invoice.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
